import React, { useEffect, useRef, useState, useMemo } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useHistory } from "@/hooks/useHistory";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import LayoutToolbar from "./layoutEditor/LayoutToolbar";
import LayoutCanvas from "./layoutEditor/LayoutCanvas";
import PdfTemplateCard from "./layoutEditor/PdfTemplateCard";
import PropertiesPanel from "./layoutEditor/PropertiesPanel";
import QRColorPicker, { QR_COLOR_LABELS } from "./layoutEditor/QRColorPicker";
import PresetLibraryPanel from "./layoutEditor/PresetLibraryPanel";
import FieldListPanel from "./layoutEditor/FieldListPanel";
import {
  SCALE,
  SAMPLE,
  FIELD_GROUPS,
  pageDims,
} from "./layoutEditor/constants";

export default function LayoutEditor() {
  const [defaults, setDefaults] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);

  // Grid snap
  const [gridStep, setGridStep] = useState(5);

  // Preset library
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");

  // QR color setting (Hitam / Biru Navy / Coklat)
  const [qrColor, setQrColor] = useState("black");
  const [qrPreviewNonce, setQrPreviewNonce] = useState(0);
  const [qrSaving, setQrSaving] = useState(false);

  const history = useHistory({}, { limit: 50 });
  const layout = history.state;
  const setLayout = history.setState;

  const dragActive = useRef(false);
  const importInputRef = useRef(null);

  useEffect(() => {
    api.get("/settings/layout").then((r) => {
      setDefaults(r.data.defaults || {});
      const merged = { ...r.data.defaults };
      for (const [k, v] of Object.entries(r.data.fields || {})) {
        merged[k] = { ...merged[k], ...v };
      }
      history.reset(merged);
    });
    reloadPresets();
    api.get("/settings/qr").then((r) => setQrColor(r.data?.color || "black")).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadPresets = () => {
    api.get("/settings/layout/presets").then((r) => setPresets(r.data)).catch(() => {});
  };

  const updateField = (id, patch) => {
    setLayout({ ...layout, [id]: { ...layout[id], ...patch } });
  };

  const commitChange = (nextLayout) => {
    history.commit(nextLayout);
  };

  const onDragStart = () => { dragActive.current = true; };
  const onDragEnd = () => {
    if (dragActive.current) {
      dragActive.current = false;
      commitChange();
    }
  };

  const onUndo = history.undo;
  const onRedo = history.redo;

  const onSave = async () => {
    setSaving(true);
    try {
      await api.post("/settings/layout", { fields: layout });
      toast.success("Layout tersimpan. PDF SPB berikutnya pakai layout ini.");
      setPreviewKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  useKeyboardShortcuts({
    "ctrl+z": onUndo,
    "ctrl+shift+z": onRedo,
    "ctrl+y": onRedo,
    "ctrl+s": onSave,
  });

  // Page config (dimensions)
  const pageCfg = layout._page || { size: "A5", orientation: "landscape" };
  const pageDim = pageDims(pageCfg);
  const canvasW = pageDim.w * SCALE;
  const canvasH = pageDim.h * SCALE;

  const updatePageCfg = (patch) => {
    const next = { ...layout, _page: { ...pageCfg, ...patch } };
    setLayout(next);
    commitChange(next);
  };

  const onAddCustomText = () => {
    const text = window.prompt("Isi teks kustom:", "Teks baru");
    if (!text) return;
    const id = `custom_${Date.now().toString(36)}`;
    const next = {
      ...layout,
      [id]: {
        x: Math.round(pageDim.w / 2), y: Math.round(pageDim.h / 2),
        size: 10, bold: false, italic: false, align: "left",
        text, font: "helvetica", color: "black",
      },
    };
    setLayout(next);
    commitChange(next);
    setSelectedId(id);
    toast.success("Teks kustom ditambahkan — klik untuk edit atau drag untuk pindahkan");
  };

  const onAddRectangle = () => {
    const id = `shape_${Date.now().toString(36)}`;
    const next = {
      ...layout,
      [id]: {
        type: "rectangle",
        x: Math.round(pageDim.w / 2 - 60),
        y: Math.round(pageDim.h / 2 - 30),
        w: 120, h: 60,
        stroke_color: "#111111",
        stroke_width: 0.8,
        fill_color: "none",
        radius: 0,
      },
    };
    setLayout(next);
    commitChange(next);
    setSelectedId(id);
    toast.success("Kotak ditambahkan — atur warna & ukuran di panel kanan");
  };

  const onAddLine = () => {
    const id = `shape_${Date.now().toString(36)}`;
    const next = {
      ...layout,
      [id]: {
        type: "line",
        x: Math.round(pageDim.w / 2 - 60),
        y: Math.round(pageDim.h / 2),
        w: 120, h: 0,
        stroke_color: "#111111",
        stroke_width: 0.8,
      },
    };
    setLayout(next);
    commitChange(next);
    setSelectedId(id);
    toast.success("Garis ditambahkan — atur panjang & ketebalan di panel kanan");
  };

  const onDeleteField = (id) => {
    if (!id?.startsWith("custom_") && !id?.startsWith("shape_")) {
      toast.error("Hanya field custom/shape yang bisa dihapus. Gunakan toggle Sembunyikan untuk field bawaan.");
      return;
    }
    const kind = id.startsWith("shape_") ? "shape" : "field custom";
    if (!window.confirm(`Hapus ${kind} "${id}"?`)) return;
    const next = { ...layout };
    delete next[id];
    setLayout(next);
    commitChange(next);
    setSelectedId(null);
  };

  const onReset = async () => {
    if (!window.confirm("Reset ke layout default? Semua perubahan tersimpan hilang.")) return;
    try {
      await api.delete("/settings/layout");
      const r = await api.get("/settings/layout");
      const merged = { ...r.data.defaults };
      setLayout(merged);
      history.commit(merged);
      toast.success("Layout direset ke default");
      setPreviewKey((k) => k + 1);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onExport = () => {
    const blob = new Blob([JSON.stringify(layout, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `spb-layout-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Layout diexport sebagai JSON");
  };

  const onImportClick = () => importInputRef.current?.click();

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("Format tidak valid");
      // Merge onto defaults to preserve any missing fields
      const merged = { ...defaults };
      for (const [k, v] of Object.entries(parsed)) {
        if (v && typeof v === "object") merged[k] = { ...defaults[k], ...v };
      }
      setLayout(merged);
      history.commit(merged);
      toast.success(`Layout diimport dari ${file.name}`);
    } catch (err) {
      toast.error(`Gagal import: ${err.message}`);
    } finally {
      e.target.value = "";
    }
  };

  // Preset library
  const onSaveAsPreset = async () => {
    const name = presetName.trim() || window.prompt("Nama preset:");
    if (!name || !name.trim()) return;
    try {
      await api.post("/settings/layout/presets", { name: name.trim(), fields: layout });
      toast.success(`Preset "${name.trim()}" disimpan`);
      setPresetName("");
      reloadPresets();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onApplyPreset = async (preset) => {
    if (!window.confirm(`Terapkan preset "${preset.name}"? Perubahan saat ini akan hilang (bisa di-undo).`)) return;
    try {
      const { data } = await api.post(`/settings/layout/presets/${preset.id}/apply`);
      const merged = { ...defaults };
      for (const [k, v] of Object.entries(data.fields || {})) {
        if (v && typeof v === "object") merged[k] = { ...defaults[k], ...v };
      }
      setLayout(merged);
      history.commit(merged);
      toast.success(`Preset "${preset.name}" diterapkan`);
      setPreviewKey((k) => k + 1);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDeletePreset = async (preset) => {
    if (!window.confirm(`Hapus preset "${preset.name}"?`)) return;
    try {
      await api.delete(`/settings/layout/presets/${preset.id}`);
      toast.success("Preset dihapus");
      reloadPresets();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onSaveQrColor = async (nextColor) => {
    const prev = qrColor;
    setQrColor(nextColor);
    setQrSaving(true);
    try {
      await api.post("/settings/qr", { color: nextColor });
      toast.success(`Warna QR di-set ke ${QR_COLOR_LABELS[nextColor] || nextColor}`);
      setQrPreviewNonce((n) => n + 1);
      setPreviewKey((k) => k + 1);
    } catch (err) {
      setQrColor(prev);
      toast.error(formatApiError(err));
    } finally {
      setQrSaving(false);
    }
  };

  const livePayload = encodeURIComponent(JSON.stringify(layout));
  const previewUrl = `${API_BASE}/settings/layout/preview.pdf?live=${livePayload}&k=${previewKey}`;

  // Memo the filtered field entries so we don't rebuild them each keystroke
  const layoutEntries = useMemo(
    () => Object.entries(layout).filter(([id]) => id !== "_page"),
    [layout]
  );

  const selected = selectedId ? layout[selectedId] : null;

  return (
    <div className="p-4 md:p-6 max-w-[1600px]">
      <LayoutToolbar
        history={history}
        onUndo={onUndo}
        onRedo={onRedo}
        onExport={onExport}
        onImportClick={onImportClick}
        onImportFile={onImportFile}
        importInputRef={importInputRef}
        previewMode={previewMode}
        setPreviewMode={setPreviewMode}
        gridStep={gridStep}
        setGridStep={setGridStep}
        pageCfg={pageCfg}
        updatePageCfg={updatePageCfg}
        onAddCustomText={onAddCustomText}
        onAddRectangle={onAddRectangle}
        onAddLine={onAddLine}
        onReset={onReset}
        onSave={onSave}
        saving={saving}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <LayoutCanvas
          previewMode={previewMode}
          previewUrl={previewUrl}
          canvasW={canvasW}
          canvasH={canvasH}
          gridStep={gridStep}
          pageCfg={pageCfg}
          pageDim={pageDim}
          layoutEntries={layoutEntries}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          updateField={updateField}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          sampleMap={SAMPLE}
        />

        {/* Side Panel */}
        <div className="space-y-4 lg:sticky lg:top-4 h-fit">
          <PdfTemplateCard onChange={() => setPreviewMode(false)} />

          <PropertiesPanel
            selectedId={selectedId}
            selected={selected}
            layout={layout}
            defaults={defaults}
            updateField={updateField}
            commitChange={commitChange}
            setLayout={setLayout}
            history={history}
            onDeleteField={onDeleteField}
          />

          <QRColorPicker
            value={qrColor}
            onChange={onSaveQrColor}
            saving={qrSaving}
            previewNonce={qrPreviewNonce}
          />

          <PresetLibraryPanel
            presets={presets}
            presetName={presetName}
            onPresetNameChange={setPresetName}
            onSaveAsPreset={onSaveAsPreset}
            onApplyPreset={onApplyPreset}
            onDeletePreset={onDeletePreset}
          />

          <FieldListPanel
            groups={FIELD_GROUPS}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </div>
      </div>
    </div>
  );
}